import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Users, Star, RotateCcw, Trash2 } from "lucide-react";

export default function AdminPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const resetFounder = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/founder-reset");
      toast.success(data.message);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const deleteUser = async (u) => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success(`Removed ${u.email}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <p className="text-muted-foreground">Loading…</p>;
  if (!data) return null;

  return (
    <div data-testid="admin-panel" className="space-y-8">
      <div className="grid md:grid-cols-4 gap-4">
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Total users</div>
          <div className="font-display text-4xl mt-2" data-testid="admin-total-users">{data.total}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Founding-20 claimed</div>
          <div className="font-display text-4xl mt-2" data-testid="admin-founders-claimed">{data.founders_claimed}/20</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Referred sign-ups</div>
          <div className="font-display text-4xl mt-2">{data.referred_signups}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Google sign-ins</div>
          <div className="font-display text-4xl mt-2">{data.google_signups}</div>
        </div>
      </div>

      <div className="rounded-2xl border border-amber-300 bg-amber-50 p-6">
        <div className="flex items-start gap-3">
          <RotateCcw className="h-5 w-5 text-amber-700 mt-0.5" strokeWidth={1.5}/>
          <div className="flex-1">
            <div className="font-display text-lg text-amber-900">Reset the Founding-20 counter</div>
            <p className="text-sm text-amber-800 mt-1">
              Clear the founding-bonus flag on all accounts so the promo starts fresh. Anyone who already received the $50 credit KEEPS it — this only resets the "spots claimed" number back toward zero. Use this if the counter got polluted during testing.
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="reset-founder-btn" variant="outline" className="mt-3 border-amber-400 text-amber-900 hover:bg-amber-100">Reset Founding-20 counter</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset the Founding-20 counter?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This clears the founding_bonus_awarded flag on every user. The counter goes back to 0/20 and the next 20 owners to publish will each earn $50. Credits already awarded are NOT clawed back.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={resetFounder} disabled={busy}>Reset</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-accent" strokeWidth={1.5}/>
          <h2 className="font-display text-2xl">All users</h2>
        </div>
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border bg-secondary/40 text-[11px] uppercase tracking-widest text-muted-foreground">
            <div className="col-span-3">Name</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-2">Role</div>
            <div className="col-span-2">Referral code</div>
            <div className="col-span-1">Founding</div>
            <div className="col-span-1 text-right">Action</div>
          </div>
          {data.users.map((u) => (
            <div key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border/60 items-center text-sm hover:bg-secondary/20">
              <div className="col-span-3 font-medium flex items-center gap-1">
                {u.name || "—"}
                {(u.qualified_referrals_count || 0) > 0 && <Star className="h-3 w-3 text-accent fill-accent" strokeWidth={1.5}/>}
              </div>
              <div className="col-span-3 text-muted-foreground truncate">{u.email}</div>
              <div className="col-span-2">
                <Badge variant="outline" className="capitalize text-xs">{u.role || "renter"}</Badge>
              </div>
              <div className="col-span-2 font-mono text-xs text-muted-foreground">{u.referral_code || "—"}</div>
              <div className="col-span-1">
                {u.founding_bonus_awarded
                  ? <Badge className="bg-accent/20 text-accent border-accent/40">#{u.founding_rank || "?"}</Badge>
                  : <span className="text-muted-foreground text-xs">—</span>}
              </div>
              <div className="col-span-1 text-right">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" data-testid={`delete-user-${u.user_id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" strokeWidth={1.5}/>
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {u.email}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This removes their login. Their docks, bookings, and referrals stay in the system but the account can no longer sign in.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => deleteUser(u)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
